import { useEffect } from 'react';
import { Alert, Button, Form, Input, Select } from 'antd';
import { MaskedSecretField, space } from '@viq-admin/ui';
import type { CrmConnectionRow, SalesforceConnectionSaveBody } from './types';

/**
 * Create/edit form for a Salesforce CRM connection. Fields, copy and rules
 * are lifted verbatim (controller ruling, p6 Task 2) from the console's
 * `create.tsx`/`edit.tsx` — merged into one mode-driven form since the two
 * pages differed only in: whether a Company select is offered (console
 * cross-tenant vs. portal own-company — modeled here as the `companies` prop,
 * present only for the console), whether the company is editable (create only
 * — this form never shows a read-only company on edit, matching the console's
 * edit page, which renders it as a `Descriptions` item OUTSIDE this form), and
 * the consumer-secret required-message text.
 *
 * The consumer-secret `required` rule is gated on `hasSecret` rather than on
 * `mode`: for a fresh `create` call (no `initial`), `hasSecret` defaults to
 * `false`, which reproduces the console create page's unconditional
 * required-on-create behavior through the SAME conditional rule the edit page
 * uses — one rule instead of two.
 *
 * `onDirtyChange` mirrors `LeadEditForm`'s pattern (`packages/screens/src/
 * leadDetail/LeadEditForm.tsx`): fires `false` once on mount (a fresh mount
 * is always pristine — the console's `ConnectionDetail` usage remounts this
 * form on every Edit click, since `editView`/`readView` are mutually
 * exclusive branches of `DetailPage`'s body), then `form.isFieldsTouched()`
 * on every value change. Also fires `false` after a SUCCESSFUL submit — but
 * only then: `onSubmit` rejecting (the caller's own save failed) is caught
 * here and swallowed (the caller already owns its own error messaging, e.g.
 * a toast) specifically so the dirty flag is NOT cleared on a failed save —
 * the operator's still-unsaved edits stay guarded.
 */
export interface SalesforceConnectionFormProps {
  mode: 'create' | 'edit';
  initial?: CrmConnectionRow;
  /** Rendered ONLY when provided — the console (cross-tenant). The portal
   * (own-company, token-scoped) omits it and the field does not render. */
  companies?: Array<{ id: string; name: string }>;
  onSubmit: (body: SalesforceConnectionSaveBody) => Promise<void>;
  submitting?: boolean;
  /** Optional — drives a caller's discard-unsaved-changes guard (e.g. the
   * console's `ConnectionDetail.dirty`). Omit when the caller doesn't track
   * dirty state (e.g. a one-shot create Drawer that just closes on submit). */
  onDirtyChange?: (dirty: boolean) => void;
}

interface FormShape {
  companyId?: string;
  consumerKey: string;
  loginUrl: string;
  apiVersion: string;
  externalIdField: string;
  consumerSecret?: string;
}

export function SalesforceConnectionForm({
  mode,
  initial,
  companies,
  onSubmit,
  submitting,
  onDirtyChange,
}: SalesforceConnectionFormProps) {
  const [form] = Form.useForm<FormShape>();
  const hasSecret = initial?.hasSecret ?? false;

  useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  // Flat-field seeding from the record's nested `config` (the console
  // edit.tsx's `initialValues={{ ...formProps.initialValues, ...record?.config }}`
  // trick) — the flat field names below resolve directly against it.
  const initialValues: Partial<FormShape> = {
    companyId: initial?.companyId,
    consumerKey: initial?.config?.consumerKey,
    loginUrl: initial?.config?.loginUrl,
    apiVersion: initial?.config?.apiVersion,
    externalIdField: initial?.config?.externalIdField,
  };

  async function handleFinish(values: FormShape) {
    const body: SalesforceConnectionSaveBody = {
      crmSource: 'salesforce',
      config: {
        consumerKey: values.consumerKey,
        loginUrl: values.loginUrl,
        apiVersion: values.apiVersion,
        externalIdField: values.externalIdField,
      },
    };
    // Defensive — the field is required whenever blank would drop the only
    // stored secret (see the `hasSecret`-gated rule below).
    if (values.consumerSecret) {
      body.secrets = { consumerSecret: values.consumerSecret };
    }
    // companyId travels only when the console's Company field is present
    // (and therefore chosen, per its `required` rule) — the portal never
    // supplies `companies`, so this form never sends a companyId for it.
    if (companies) {
      body.companyId = values.companyId;
    }
    try {
      await onSubmit(body);
      onDirtyChange?.(false);
    } catch {
      // The caller's onSubmit owns its own error messaging (the console's
      // create/edit wrappers catch + toast) — swallow here so a failed save
      // doesn't surface as an unhandled rejection from antd's Form, and,
      // critically, do NOT reset dirty: the operator's edits are still
      // unsaved and should stay guarded.
    }
  }

  return (
    <Form<FormShape>
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={() => onDirtyChange?.(form.isFieldsTouched())}
      onFinish={handleFinish}
    >
      {companies && (
        <Form.Item
          label="Company"
          name="companyId"
          rules={[{ required: true, message: 'Company is required' }]}
        >
          <Select
            placeholder="Select company…"
            options={companies.map((c) => ({ label: c.name, value: c.id }))}
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      )}

      <Form.Item
        label="Consumer Key"
        name="consumerKey"
        rules={[{ required: true }]}
        extra="Salesforce Setup → App Manager → OAuth"
      >
        <Input placeholder="3MVG9..." />
      </Form.Item>

      {/* The pattern rules below mirror the backend's SalesforceConfigSchema
          (viq-backend shared/src/types/crm.ts) — https-only loginUrl,
          ^v\d+\.\d+$ apiVersion, bare-API-name externalIdField. Anything
          looser here reaches the backend and comes back as a bare
          'Validation failed' 422, which names no field. */}
      <Form.Item
        label="Login URL"
        name="loginUrl"
        rules={[
          { required: true },
          { type: 'url' },
          { pattern: /^https:\/\//, message: 'Login URL must start with https://' },
        ]}
        extra="Your Salesforce My Domain URL"
      >
        <Input placeholder="https://mycompany.my.salesforce.com" />
      </Form.Item>

      <Form.Item
        label="API Version"
        name="apiVersion"
        rules={[
          { required: true },
          { pattern: /^v\d+\.\d+$/, message: 'API Version must look like v60.0' },
        ]}
        extra="e.g. v60.0"
      >
        <Input placeholder="v60.0" />
      </Form.Item>

      <Form.Item
        label="External ID Field"
        name="externalIdField"
        rules={[
          { required: true },
          {
            pattern: /^[A-Za-z0-9_]+$/,
            message: 'External ID Field must use only letters, digits, and underscores',
          },
        ]}
      >
        <Input placeholder="VendoIQ_Person_Id__c" />
      </Form.Item>

      <Form.Item
        label="Consumer Secret"
        name="consumerSecret"
        rules={
          !hasSecret
            ? [
                {
                  required: true,
                  message:
                    mode === 'create'
                      ? 'Consumer Secret is required for new connections'
                      : 'Consumer Secret is required',
                },
              ]
            : []
        }
      >
        <MaskedSecretField hasSecret={hasSecret} />
      </Form.Item>

      {hasSecret && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: space.lg }}
          message="A consumer secret is already stored. Leave the field blank to keep it. Type a new value to overwrite."
        />
      )}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
}
